import { useModuleAccess } from "@/hooks/useModuleAccess";
import { workflowActionModule } from "../../../shared/workflow-trigger-modules";
import { useTranslation } from "react-i18next";
/* eslint-disable react-refresh/only-export-components */
// ════════════════════════════════════════════════════════════════════
// NodeConfigPanel — right sidebar that opens when a step node is
// selected. Renders a per-step-type config form. Common fields (name,
// role assignment, SLA, delay) live in a shared block; type-specific
// params have focused sub-forms. Power-user JSON escape hatch at the
// bottom covers anything the form doesn't surface.
// ════════════════════════════════════════════════════════════════════
import { useEffect, useState, type ReactNode } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Trash2, Save, X, AlertCircle, Bot } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { WorkflowStep } from "@/lib/api";
import { STEP_META, tintClasses } from "./nodes";

const ROLES = [
  { value: "hr", label: "HR" },
  { value: "admin", label: "Admin" },
  { value: "c_level", label: "C-level" },
  { value: "line_manager", label: "Line Manager" },
  { value: "employee", label: "Employee" },
  { value: "initiator", label: "Initiator" },
];

export const SERVICE_ACTIONS = [
  { value: "generate_document",              label: "Generate Document",                group: "Documents" },
  { value: "generate_contract_renewal",      label: "Generate Contract Renewal",        group: "Documents" },
  { value: "generate_internship_certificate",label: "Generate Internship Certificate",  group: "Documents" },
  { value: "request_signature",              label: "Request Document Signature",       group: "Documents" },
  { value: "update_salary",                  label: "Update Salary",                    group: "Employee" },
  { value: "update_employee_field",          label: "Update Employee Field",            group: "Employee" },
  { value: "start_offboarding",              label: "Start Offboarding",                group: "Employee" },
  { value: "approve_leave_request",          label: "Approve Leave Request",            group: "Leave" },
  { value: "reject_leave_request",           label: "Reject Leave Request",             group: "Leave" },
  { value: "adjust_leave_balance",           label: "Adjust Leave Balance",             group: "Leave" },
  { value: "approve_timesheet",              label: "Approve Timesheet",                group: "Timekeeping" },
  { value: "reject_timesheet",               label: "Reject Timesheet",                 group: "Timekeeping" },
  { value: "return_timesheet_for_changes",   label: "Return Timesheet for Changes",     group: "Timekeeping" },
  { value: "create_payroll_adjustment",      label: "Create Payroll Adjustment",        group: "Payroll" },
  { value: "create_onboarding_session",      label: "Start Onboarding Session",         group: "Onboarding" },
  { value: "complete_onboarding_task",       label: "Complete Onboarding Task",         group: "Onboarding" },
  { value: "change_application_status",      label: "Change Application Status",        group: "Recruitment" },
  { value: "update_kpi_status",              label: "Update KPI Status",                group: "Performance" },
  { value: "publish_survey",                 label: "Publish Survey",                   group: "Surveys" },
  { value: "assign_survey_to_employee",      label: "Assign Survey to Employee",        group: "Surveys" },
  { value: "assign_equipment_from_stock",    label: "Assign Equipment from Stock",      group: "Equipment" },
  { value: "send_notification",              label: "Send Notification",                group: "Notifications" },
  { value: "send_email",                     label: "Send Email",                       group: "Notifications" },
  { value: "send_reminder",                  label: "Send Reminder",                    group: "Notifications" },
  { value: "escalate_task",                  label: "Escalate Task",                    group: "Notifications" },
  { value: "create_calendar_event",          label: "Create Calendar Event",            group: "Calendar" },
];

// Operators supported by the engine's evaluateRule() (workflow-engine.ts).
const CONDITION_OPERATORS = [
  { value: "eq",            label: "equals" },
  { value: "neq",           label: "not equals" },
  { value: "gt",            label: "greater than" },
  { value: "gte",           label: "greater or equal" },
  { value: "lt",            label: "less than" },
  { value: "lte",           label: "less or equal" },
  { value: "contains",      label: "contains" },
  { value: "not_contains",  label: "does not contain" },
  { value: "starts_with",   label: "starts with" },
  { value: "ends_with",     label: "ends with" },
  { value: "in",            label: "is one of (comma-sep)" },
  { value: "not_in",        label: "is not one of (comma-sep)" },
  { value: "between",       label: "between (lo,hi)" },
  { value: "date_before",   label: "date before (YYYY-MM-DD)" },
  { value: "date_after",    label: "date after (YYYY-MM-DD)" },
  { value: "days_until_lt", label: "days until is less than" },
  { value: "days_until_gt", label: "days until is greater than" },
  { value: "is_empty",      label: "is empty" },
  { value: "is_not_empty",  label: "is not empty" },
];
const NO_VALUE_OPS = new Set(["is_empty", "is_not_empty"]);

type ConditionRule = { field: string; operator: string; value: any };

type Draft = {
  name: string;
  assign_to_role: string;
  assign_to_user_id: number | null;
  config: Record<string, any>;
  outputs_count: number;
};

// ── Reusable config-field primitives (module scope so they don't remount) ──
function CfgHint({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-700 dark:text-sky-300 flex gap-2">
      <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
      <div className="text-muted-foreground">{children}</div>
    </div>
  );
}

function CfgText({
  label, optional, placeholder, value, onChange, type = "text", textarea,
}: {
  label: string;
  optional?: boolean;
  placeholder?: string;
  value: any;
  onChange: (v: string) => void;
  type?: string;
  textarea?: boolean;
}) {
  return (
    <div>
      <Label className="text-xs">
        {label}
        {optional && <span className="text-muted-foreground"> (optional)</span>}
      </Label>
      {textarea ? (
        <textarea
          className="w-full mt-1 rounded-md border border-input bg-background px-2 py-1.5 text-xs min-h-[72px]"
          placeholder={placeholder}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      ) : (
        <Input
          className="h-8 mt-1 text-xs"
          type={type}
          placeholder={placeholder}
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function CfgRole({
  label = "Recipient role", value, onChange,
}: {
  label?: string;
  value: any;
  onChange: (v: string) => void;
}) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Select value={value || "employee"} onValueChange={onChange}>
        <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
        <SelectContent>
          {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

export function NodeConfigPanel({
  step, onSave, onDelete, onClose, saving,
}: {
  step: WorkflowStep;
  onSave: (patch: Partial<WorkflowStep>) => Promise<void> | void;
  onDelete: () => Promise<void> | void;
  onClose: () => void;
  saving?: boolean;
}) {
  const { toast } = useToast();
  const { t } = useTranslation();
  const { canAccess } = useModuleAccess();
  const availableActions = SERVICE_ACTIONS.filter(action => { const owner = workflowActionModule(action.value); return !owner || canAccess(owner); });
  const meta = STEP_META[step.step_type];
  const tint = tintClasses(meta?.tint || "slate");

  // Local draft state — committed via Save.
  const [draft, setDraft] = useState<Draft>(() => ({
    name: step.name,
    assign_to_role: step.assign_to_role || "",
    assign_to_user_id: step.assign_to_user_id,
    config: { ...(step.config || {}) },
    outputs_count: step.outputs_count || 1,
  }));

  // Reset draft when a different step is selected.
  useEffect(() => {
    setDraft({
      name: step.name,
      assign_to_role: step.assign_to_role || "",
      assign_to_user_id: step.assign_to_user_id,
      config: { ...(step.config || {}) },
      outputs_count: step.outputs_count || 1,
    });
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Advanced JSON escape hatch — kept as a parallel string so the user can
  // type freely and we only re-parse on Save. `jsonTouched` flips true the
  // moment the user types in the textarea; otherwise we leave the form
  // fields as the source of truth so changes in the dropdowns above don't
  // get silently clobbered by a stale snapshot of step.config.
  const [jsonDraft, setJsonDraft] = useState(() => JSON.stringify(step.config || {}, null, 2));
  const [jsonTouched, setJsonTouched] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);
  useEffect(() => {
    setJsonDraft(JSON.stringify(step.config || {}, null, 2));
    setJsonTouched(false);
    setJsonError(null);
  }, [step.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchConfig = (patch: Record<string, any>) =>
    setDraft((d) => ({ ...d, config: { ...d.config, ...patch } }));

  const handleSave = async () => {
    // Form fields are the primary source. JSON textarea only overrides when
    // the user has explicitly edited it.
    let mergedConfig = draft.config;
    if (jsonTouched) {
      try {
        const parsed = JSON.parse(jsonDraft);
        if (parsed && typeof parsed === "object") mergedConfig = parsed;
      } catch (e: any) {
        setJsonError(e.message);
        toast({ title: "Invalid JSON in advanced config", description: e.message, variant: "destructive" });
        return;
      }
    }
    await onSave({
      name: draft.name,
      assign_to_role: draft.assign_to_role || null,
      assign_to_user_id: draft.assign_to_user_id,
      config: mergedConfig,
      outputs_count: draft.outputs_count,
    });
    // Close the panel after a successful save — matches n8n UX.
    onClose();
  };

  const showAssignment = step.step_type === "approval" || step.step_type === "task";
  const showSLAandDelay = showAssignment;
  const Icon = meta?.icon;

  return (
    <div className="w-[360px] h-full border-l border-border bg-card flex flex-col">
      <div className={`flex items-center gap-2 px-4 py-3 border-b ${tint.bg} ${tint.border}`}>
        {Icon && <Icon className={`w-4 h-4 ${tint.text}`} />}
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${tint.text}`}>
          {meta?.label || step.step_type}
        </span>
        <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 h-4">
          step #{step.id}
        </Badge>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-4">
        {/* ── Name ─────────────────────────────────────────── */}
        <div>
          <Label className="text-xs">Step name</Label>
          <Input
            className="h-8 mt-1"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Approve salary increase"
          />
        </div>

        {/* ── Assignment (approval/task only) ───────────────── */}
        {showAssignment && (
          <div>
            <Label className="text-xs">Assign to role</Label>
            <Select
              value={draft.assign_to_role || "none"}
              onValueChange={(v) => setDraft({ ...draft, assign_to_role: v === "none" ? "" : v })}
            >
              <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— unassigned —</SelectItem>
                {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* ── SLA / delay (approval/task) ───────────────────── */}
        {showSLAandDelay && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Delay (days)</Label>
              <Input
                className="h-8 mt-1 text-xs"
                type="number"
                min={0}
                placeholder="0"
                value={draft.config.delay_days ?? ""}
                onChange={(e) => patchConfig({ delay_days: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div>
              <Label className="text-xs">SLA (hours)</Label>
              <Input
                className="h-8 mt-1 text-xs"
                type="number"
                min={0}
                placeholder="(no SLA)"
                value={draft.config.sla_hours ?? ""}
                onChange={(e) => patchConfig({ sla_hours: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Assignment strategy</Label>
              <Select
                value={draft.config.assignment_strategy ?? "all"}
                onValueChange={(v) => patchConfig({ assignment_strategy: v })}
              >
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Broadcast to all</SelectItem>
                  <SelectItem value="least_busy">Least-busy user</SelectItem>
                  <SelectItem value="round_robin">Round-robin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* ── Service action picker ────────────────────────── */}
        {step.step_type === "service_action" && (
          <>
            <div className="rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-[11px] text-violet-700 dark:text-violet-300 flex gap-2">
              <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Runs automatically.</p>
                <p className="text-muted-foreground mt-0.5">
                  Service actions don't have an assignee — the engine executes them on the platform's behalf.
                  Side effects (e.g. document <code className="font-mono">requested_by</code>, audit log) are credited to the workflow's initiator.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Action</Label>
              <Select
                value={draft.config.action || ""}
                onValueChange={(v) => patchConfig({ action: v })}
              >
                <SelectTrigger className="h-8 mt-1 text-xs">
                  <SelectValue placeholder="Pick an action…" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from(new Set(availableActions.map((a) => a.group))).map((group) => (
                    <div key={group}>
                      <p className="text-[10px] font-semibold text-muted-foreground px-2 py-1 uppercase tracking-wide">{group}</p>
                      {availableActions.filter((a) => a.group === group).map((a) => (
                        <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              {draft.config.action && (
              <p className="text-[11px] text-muted-foreground mt-1">
                Configure the action's parameters below.
              </p>
            )}
            </div>

            {/* ── generate_document params ──────────────────── */}
            {draft.config.action === "generate_document" && (
              <>
                <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-700 dark:text-sky-300 flex gap-2">
                  <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium">Document created automatically.</p>
                    <p className="text-muted-foreground mt-0.5">
                      When this step runs the engine inserts a new document record in the employee's <strong>Documents</strong> tab with status <strong>Pending</strong>. HR can then open it, fill in details, and request a signature.
                    </p>
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Document type</Label>
                  <Select
                    value={draft.config.docType || ""}
                    onValueChange={(v) => patchConfig({ docType: v })}
                  >
                    <SelectTrigger className="h-8 mt-1 text-xs">
                      <SelectValue placeholder="Pick a document type…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Contract">Contract</SelectItem>
                      <SelectItem value="Annex">Annex / Amendment</SelectItem>
                      <SelectItem value="Bank Credit">Bank Credit Letter</SelectItem>
                      <SelectItem value="Criminal Record">Criminal Record Request</SelectItem>
                      <SelectItem value="Sick Leave">Sick Leave Certificate</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Document name <span className="text-muted-foreground">(optional — defaults to document type)</span></Label>
                  <Input
                    className="h-8 mt-1 text-xs"
                    placeholder={draft.config.docType || "e.g. Employment Contract 2025"}
                    value={draft.config.docName || ""}
                    onChange={(e) => patchConfig({ docName: e.target.value })}
                  />
                </div>
              </>
            )}

            {/* ── generate_contract_renewal params ─────────── */}
            {draft.config.action === "generate_contract_renewal" && (
              <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-700 dark:text-sky-300 flex gap-2">
                <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Contract renewal document created automatically.</p>
                  <p className="text-muted-foreground mt-0.5">
                    Creates a <strong>Contract</strong> document with status <strong>Pending</strong> in the employee's Documents tab. No additional parameters are required.
                  </p>
                </div>
              </div>
            )}

            {/* ── generate_internship_certificate params ────── */}
            {draft.config.action === "generate_internship_certificate" && (
              <div className="rounded-md border border-sky-500/30 bg-sky-500/5 px-3 py-2 text-[11px] text-sky-700 dark:text-sky-300 flex gap-2">
                <Bot className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">Internship certificate created automatically.</p>
                  <p className="text-muted-foreground mt-0.5">
                    Creates a certificate document with status <strong>Pending</strong> in the employee's Documents tab.
                  </p>
                </div>
              </div>
            )}

            {/* ── request_signature params ──────────────────── */}
            {draft.config.action === "request_signature" && (
              <>
                <CfgText label="Document name" placeholder="Document for Signature"
                  value={draft.config.docName} onChange={(v) => patchConfig({ docName: v })} />
                <CfgText label="Document template ID" optional type="number"
                  value={draft.config.documentTemplateId} onChange={(v) => patchConfig({ documentTemplateId: v })} />
                <CfgHint>The employee is taken from the workflow trigger context.</CfgHint>
              </>
            )}

            {/* ── update_salary params ──────────────────────── */}
            {draft.config.action === "update_salary" && (
              <CfgHint>Salary values (amount, currency, frequency) are taken automatically from the workflow trigger context.</CfgHint>
            )}

            {/* ── update_employee_field params ──────────────── */}
            {draft.config.action === "update_employee_field" && (
              <>
                <CfgText label={t("workflows.params.employeeField")} placeholder="job_title, location, employment_type, reports_to_id"
                  value={draft.config.field} onChange={(v) => patchConfig({ field: v })} />
                <CfgText label={t("workflows.params.employeeValue")} placeholder={t("workflows.params.reportingValueHint")}
                  value={draft.config.value} onChange={(v) => patchConfig({ value: v })} />
                {draft.config.field === "reports_to_id" && <CfgHint>{t("workflows.params.reportingValueHint")}</CfgHint>}
              </>
            )}

            {/* ── start_offboarding params ──────────────────── */}
            {draft.config.action === "start_offboarding" && (
              <>
                <CfgText label="Reason" placeholder="Initiated by workflow"
                  value={draft.config.reason} onChange={(v) => patchConfig({ reason: v })} />
                <CfgText label="Exit date" optional type="date"
                  value={draft.config.exitDate} onChange={(v) => patchConfig({ exitDate: v })} />
                <CfgHint>The employee is taken from the workflow trigger context.</CfgHint>
              </>
            )}

            {/* ── approve_leave_request params ──────────────── */}
            {draft.config.action === "approve_leave_request" && (
              <CfgHint>The leave request ID is taken automatically from the trigger context (leave_submitted event).</CfgHint>
            )}

            {/* ── reject_leave_request params ───────────────── */}
            {draft.config.action === "reject_leave_request" && (
              <>
                <CfgText label="Rejection reason" textarea placeholder="Reason shown to the employee"
                  value={draft.config.reason} onChange={(v) => patchConfig({ reason: v })} />
                <CfgHint>The leave request ID is taken automatically from the trigger context.</CfgHint>
              </>
            )}

            {/* ── timesheet approve / reject / return params ── */}
            {draft.config.action === "approve_timesheet" && (
              <CfgHint>The timesheet ID is taken automatically from the trigger context. This marks the timesheet as Approved.</CfgHint>
            )}
            {(draft.config.action === "reject_timesheet" || draft.config.action === "return_timesheet_for_changes") && (
              <>
                <CfgText label="Reason" textarea placeholder="Reason shown to the employee"
                  value={draft.config.reason} onChange={(v) => patchConfig({ reason: v })} />
                <CfgHint>The timesheet ID is taken automatically from the trigger context.</CfgHint>
              </>
            )}

            {/* ── adjust_leave_balance params ───────────────── */}
            {draft.config.action === "adjust_leave_balance" && (
              <>
                <CfgText label="Leave type name" placeholder="e.g. Annual Leave"
                  value={draft.config.leaveTypeName} onChange={(v) => patchConfig({ leaveTypeName: v })} />
                <CfgText label="Adjustment (days)" type="number" placeholder="positive to add, negative to subtract"
                  value={draft.config.adjustment} onChange={(v) => patchConfig({ adjustment: v })} />
                <CfgHint>Use a positive number to add days, negative to subtract. Leave type name must match exactly.</CfgHint>
              </>
            )}

            {/* ── create_payroll_adjustment params ──────────── */}
            {draft.config.action === "create_payroll_adjustment" && (
              <>
                <div>
                  <Label className="text-xs">Adjustment type</Label>
                  <Select value={draft.config.adjustmentType || "bonus"} onValueChange={(v) => patchConfig({ adjustmentType: v })}>
                    <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bonus">Bonus</SelectItem>
                      <SelectItem value="deduction">Deduction</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <CfgText label="Amount" type="number" placeholder="0"
                  value={draft.config.amount} onChange={(v) => patchConfig({ amount: v })} />
                <CfgText label="Description" optional placeholder="e.g. Performance bonus"
                  value={draft.config.description} onChange={(v) => patchConfig({ description: v })} />
              </>
            )}

            {/* ── create_onboarding_session params ──────────── */}
            {draft.config.action === "create_onboarding_session" && (
              <>
                <CfgText label="Template ID" optional type="number" placeholder="Onboarding template ID"
                  value={draft.config.templateId} onChange={(v) => patchConfig({ templateId: v })} />
                <CfgHint>The employee is taken from the workflow trigger context.</CfgHint>
              </>
            )}

            {/* ── complete_onboarding_task params ───────────── */}
            {draft.config.action === "complete_onboarding_task" && (
              <CfgHint>The session ID and task ID are taken automatically from the trigger context.</CfgHint>
            )}

            {/* ── change_application_status params ──────────── */}
            {draft.config.action === "change_application_status" && (
              <>
                <CfgText label="New status" placeholder="e.g. interview, hired, rejected"
                  value={draft.config.newStatus} onChange={(v) => patchConfig({ newStatus: v })} />
                <CfgHint>The application ID is taken from the trigger context when left to the workflow.</CfgHint>
              </>
            )}

            {/* ── update_kpi_status params ──────────────────── */}
            {draft.config.action === "update_kpi_status" && (
              <>
                <CfgText label="New status" placeholder="e.g. on_track, at_risk, completed"
                  value={draft.config.newStatus} onChange={(v) => patchConfig({ newStatus: v })} />
                <CfgText label="Progress (%)" optional type="number" placeholder="0–100"
                  value={draft.config.progress} onChange={(v) => patchConfig({ progress: v })} />
                <CfgHint>The KPI is taken from the trigger context.</CfgHint>
              </>
            )}

            {/* ── publish_survey params ─────────────────────── */}
            {draft.config.action === "publish_survey" && (
              <CfgText label="Survey ID" optional type="number" placeholder="defaults to trigger context"
                value={draft.config.surveyId} onChange={(v) => patchConfig({ surveyId: v })} />
            )}

            {/* ── assign_survey_to_employee params ──────────── */}
            {draft.config.action === "assign_survey_to_employee" && (
              <>
                <CfgText label="Survey ID" type="number" placeholder="Survey to assign"
                  value={draft.config.surveyId} onChange={(v) => patchConfig({ surveyId: v })} />
                <CfgHint>The employee is taken from the trigger context.</CfgHint>
              </>
            )}

            {/* ── assign_equipment_from_stock params ────────── */}
            {draft.config.action === "assign_equipment_from_stock" && (
              <>
                <CfgText label="Category" placeholder="e.g. Laptop, Phone"
                  value={draft.config.category} onChange={(v) => patchConfig({ category: v })} />
                <CfgHint>The employee is taken from the trigger context.</CfgHint>
              </>
            )}

            {/* ── escalate_task params ──────────────────────── */}
            {draft.config.action === "escalate_task" && (
              <>
                <CfgRole label="Escalate to role" value={draft.config.escalateToRole || "admin"}
                  onChange={(v) => patchConfig({ escalateToRole: v })} />
                <CfgHint>The task is taken from the trigger context.</CfgHint>
              </>
            )}

            {/* ── send_notification params ──────────────────── */}
            {draft.config.action === "send_notification" && (
              <>
                <CfgRole value={draft.config.recipientRole} onChange={(v) => patchConfig({ recipientRole: v })} />
                <CfgText label="Title" placeholder="Notification title"
                  value={draft.config.notifTitle} onChange={(v) => patchConfig({ notifTitle: v })} />
                <CfgText label="Message" textarea placeholder="Notification message"
                  value={draft.config.notifMessage} onChange={(v) => patchConfig({ notifMessage: v })} />
              </>
            )}

            {/* ── send_email params ─────────────────────────── */}
            {draft.config.action === "send_email" && (
              <>
                <CfgRole value={draft.config.recipientRole} onChange={(v) => patchConfig({ recipientRole: v })} />
                <CfgText label="Subject" placeholder="Email subject"
                  value={draft.config.subject} onChange={(v) => patchConfig({ subject: v })} />
                <CfgText label="Body" textarea placeholder="Email body"
                  value={draft.config.body} onChange={(v) => patchConfig({ body: v })} />
              </>
            )}

            {/* ── send_reminder params ──────────────────────── */}
            {draft.config.action === "send_reminder" && (
              <>
                <CfgRole value={draft.config.recipientRole} onChange={(v) => patchConfig({ recipientRole: v })} />
                <CfgText label="Message" textarea placeholder="Reminder message"
                  value={draft.config.message} onChange={(v) => patchConfig({ message: v })} />
                <div>
                  <Label className="text-xs">Channel</Label>
                  <Select value={draft.config.channel || "notification"} onValueChange={(v) => patchConfig({ channel: v })}>
                    <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notification">In-app only</SelectItem>
                      <SelectItem value="email">Email only</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}

            {/* ── create_calendar_event params ──────────────── */}
            {draft.config.action === "create_calendar_event" && (
              <>
                <CfgText label="Title" placeholder="Event title"
                  value={draft.config.title} onChange={(v) => patchConfig({ title: v })} />
                <CfgText label="Date" optional type="date" placeholder="defaults to context startDate"
                  value={draft.config.date} onChange={(v) => patchConfig({ date: v })} />
                <CfgText label="Category" optional placeholder="meeting"
                  value={draft.config.category} onChange={(v) => patchConfig({ category: v })} />
                <CfgText label="Description" optional textarea
                  value={draft.config.description} onChange={(v) => patchConfig({ description: v })} />
              </>
            )}
          </>
        )}

        {/* ── Notification quick form ──────────────────────── */}
        {step.step_type === "notification" && (
          <>
            <div>
              <Label className="text-xs">Recipient role</Label>
              <Select
                value={draft.config.recipientRole || "employee"}
                onValueChange={(v) => patchConfig({ recipientRole: v })}
              >
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Title</Label>
              <Input className="h-8 mt-1 text-xs" value={draft.config.notifTitle || ""}
                     onChange={(e) => patchConfig({ notifTitle: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Message</Label>
              <Input className="h-8 mt-1 text-xs" value={draft.config.notifMessage || ""}
                     onChange={(e) => patchConfig({ notifMessage: e.target.value })} />
            </div>
          </>
        )}

        {/* ── Condition quick form ─────────────────────────── */}
        {step.step_type === "condition" && (() => {
          // Derive the rule list. Prefer config.rules; otherwise seed a single
          // rule from any legacy config.field/operator/equals so existing
          // conditions migrate seamlessly on first edit.
          const rules: ConditionRule[] =
            Array.isArray(draft.config.rules) && draft.config.rules.length > 0
              ? draft.config.rules
              : [{
                  field: draft.config.field || "",
                  operator: draft.config.operator || "eq",
                  value: draft.config.equals ?? draft.config.value ?? "",
                }];
          const combinator: string = draft.config.combinator || "AND";

          // Writing rules clears the legacy single-rule keys so the two
          // representations can't drift (engine prefers config.rules anyway).
          const writeRules = (next: ConditionRule[]) =>
            patchConfig({ rules: next, combinator, field: undefined, operator: undefined, equals: undefined, value: undefined });
          const updateRule = (i: number, patch: Partial<ConditionRule>) =>
            writeRules(rules.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
          const addRule = () => writeRules([...rules, { field: "", operator: "eq", value: "" }]);
          const removeRule = (i: number) => writeRules(rules.filter((_, idx) => idx !== i));

          return (
            <>
              {rules.length > 1 && (
                <div className="flex items-center gap-2">
                  <Label className="text-xs">Match</Label>
                  <Select value={combinator} onValueChange={(v) => patchConfig({ combinator: v })}>
                    <SelectTrigger className="h-7 w-[150px] text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="AND">all rules (AND)</SelectItem>
                      <SelectItem value="OR">any rule (OR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {rules.map((rule, i) => {
                const showValue = !NO_VALUE_OPS.has(rule.operator);
                return (
                  <div key={i} className="rounded-md border border-border bg-muted/20 p-2 space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        {i === 0 ? "If" : combinator}
                      </span>
                      {rules.length > 1 && (
                        <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-red-500"
                          onClick={() => removeRule(i)}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                    <Input className="h-8 text-xs" placeholder="Field — e.g. amount, leaveType, previousOutcome"
                      value={rule.field}
                      onChange={(e) => updateRule(i, { field: e.target.value })} />
                    <div className={showValue ? "grid grid-cols-2 gap-2" : ""}>
                      <Select value={rule.operator} onValueChange={(v) => updateRule(i, { operator: v })}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CONDITION_OPERATORS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {showValue && (
                        <Input className="h-8 text-xs" placeholder="Value"
                          value={rule.value ?? ""}
                          onChange={(e) => updateRule(i, { value: e.target.value })} />
                      )}
                    </div>
                  </div>
                );
              })}

              <Button variant="outline" size="sm" className="h-7 text-xs w-full" onClick={addRule}>
                + Add rule
              </Button>

              <p className="text-[11px] text-muted-foreground">
                Fields read from the workflow trigger context (e.g. <code className="font-mono">amount</code>,{" "}
                <code className="font-mono">leaveType</code>) plus <code className="font-mono">previousOutcome</code>{" "}
                / <code className="font-mono">previousStepNotes</code> from the prior step. Connect this node's{" "}
                <span className="text-emerald-600 font-medium">true</span> and{" "}
                <span className="text-rose-600 font-medium">false</span> handles to subsequent steps.
              </p>
            </>
          );
        })()}

        {/* ── Parallel / Switch outputs count ──────────────── */}
        {(step.step_type === "parallel" || step.step_type === "switch") && (
          <div>
            <Label className="text-xs">
              Output branches{step.step_type === "switch" ? " (cases)" : ""}
            </Label>
            <Input
              className="h-8 mt-1 text-xs"
              type="number"
              min={2}
              max={8}
              value={draft.outputs_count}
              onChange={(e) => setDraft({ ...draft, outputs_count: Math.max(2, Math.min(8, Number(e.target.value) || 2)) })}
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              {step.step_type === "parallel"
                ? "All branches fire simultaneously."
                : "Each case routes to a different downstream node."}
            </p>
          </div>
        )}

        {/* ── Merge wait count ─────────────────────────────── */}
        {step.step_type === "merge" && (
          <div>
            <Label className="text-xs">Wait for N inbound completions</Label>
            <Input
              className="h-8 mt-1 text-xs"
              type="number"
              min={1}
              placeholder="(all inbound)"
              value={draft.config.wait_for_count ?? ""}
              onChange={(e) => patchConfig({ wait_for_count: e.target.value ? Number(e.target.value) : undefined })}
            />
          </div>
        )}

        {/* ── Delay timer ──────────────────────────────────── */}
        {step.step_type === "delay" && (
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Days</Label>
              <Input
                className="h-8 mt-1 text-xs"
                type="number" min={0}
                value={draft.config.delay_days ?? ""}
                onChange={(e) => patchConfig({ delay_days: e.target.value ? Number(e.target.value) : undefined })}
              />
            </div>
            <div>
              <Label className="text-xs">Count from</Label>
              <Select
                value={draft.config.delay_type ?? "after_trigger"}
                onValueChange={(v) => patchConfig({ delay_type: v })}
              >
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="after_trigger">After trigger</SelectItem>
                  <SelectItem value="before_due_date">Before due date</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* ── Advanced JSON escape hatch ───────────────────── */}
        <details className="rounded-lg border border-border bg-muted/30 p-2 open:bg-muted/40 transition-colors">
          <summary className="text-xs font-medium text-muted-foreground cursor-pointer select-none px-1 py-0.5">
            Advanced — raw JSON config
          </summary>
          <textarea
            className="w-full mt-2 font-mono text-[11px] rounded-md border border-input bg-background px-2 py-2 min-h-[140px]"
            value={jsonDraft}
            onChange={(e) => { setJsonDraft(e.target.value); setJsonTouched(true); setJsonError(null); }}
            spellCheck={false}
          />
          {jsonError && (
            <p className="text-[11px] text-rose-600 mt-1 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 shrink-0" />
              <span>{jsonError}</span>
            </p>
          )}
          <p className="text-[10px] text-muted-foreground mt-1">
            Edit any config key directly. Saving overrides the form fields above with the JSON content.
          </p>
        </details>
      </div>

      {/* ── Footer ─────────────────────────────────────────── */}
      <div className="border-t px-4 py-3 flex items-center gap-2">
        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => onDelete()}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={onClose}>{t("pilot.canvas.Cancel")}</Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? t("pilot.canvas.Saving…") : t("pilot.canvas.Save")}
        </Button>
      </div>
    </div>
  );
}
